/**
 * localStorage persistence for per-Collection chat transcripts.
 *
 * Each Collection gets its own storage key so reloading the page (or
 * coming back days later) restores the conversation as the user left it.
 * The sidebar exposes a "Clear" affordance that wipes the entry.
 *
 * Viewer-only, never synced to the server — mirrors the pattern used by
 * `omnibar-persistence.ts`, `author-filter-store.ts`, and friends.
 */
import { ConfiguredLogger } from "@/lib/logger";
const logger = new ConfiguredLogger({ caller: "CollectionChatPersistence" });
const STORAGE_KEY_PREFIX = "viewer.collectionChat.v1.";
// Cap so a runaway conversation can't blow past the per-origin
// localStorage budget. The send path also trims history before replaying
// to the backend; this is the storage-side belt to that suspenders.
const MAX_PERSISTED_MESSAGES = 200;
function storageKey(collectionId) {
    return `${STORAGE_KEY_PREFIX}${collectionId}`;
}
function isPersistedMessage(value) {
    if (value === null || typeof value !== "object")
        return false;
    const v = value;
    if (typeof v.id !== "string")
        return false;
    if (v.role !== "user" && v.role !== "assistant")
        return false;
    if (typeof v.content !== "string")
        return false;
    if (v.referencedBlockIds !== undefined) {
        if (Array.isArray(v.referencedBlockIds) === false)
            return false;
        if (v.referencedBlockIds.some((x) => typeof x !== "string"))
            return false;
    }
    return true;
}
export function loadCollectionChatMessages(collectionId) {
    if (typeof window === "undefined")
        return [];
    try {
        const raw = window.localStorage.getItem(storageKey(collectionId));
        if (!raw)
            return [];
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) === false)
            return [];
        return parsed.filter(isPersistedMessage);
    }
    catch (e) {
        logger.warn("Failed to load collection chat transcript", {
            collectionId,
            error: e instanceof Error ? e.message : String(e),
        });
        return [];
    }
}
export function saveCollectionChatMessages(collectionId, messages) {
    if (typeof window === "undefined")
        return;
    try {
        const key = storageKey(collectionId);
        // Empty arrays clear the key so a fresh "Clear" doesn't leave a
        // dangling `[]` entry rattling around in localStorage.
        if (messages.length === 0) {
            window.localStorage.removeItem(key);
            return;
        }
        const trimmed = messages.slice(-MAX_PERSISTED_MESSAGES);
        window.localStorage.setItem(key, JSON.stringify(trimmed));
    }
    catch (e) {
        logger.warn("Failed to persist collection chat transcript", {
            collectionId,
            error: e instanceof Error ? e.message : String(e),
        });
    }
}
export function clearCollectionChatMessages(collectionId) {
    if (typeof window === "undefined")
        return;
    try {
        window.localStorage.removeItem(storageKey(collectionId));
    }
    catch (e) {
        logger.warn("Failed to clear collection chat transcript", {
            collectionId,
            error: e instanceof Error ? e.message : String(e),
        });
    }
}
